import * as React from "react";
import { useNavigate } from "react-router-dom";
import { notification, Modal, Input, InputRef } from "antd";
import { request } from "../../../services/cpln";
import { MarketplaceReleaseInstance } from "../types/release";
import { getMarketplaceErrorMessage } from "../utils";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectOrg } from "../../../store/contextSlice";
import { addNotification } from "../../../store/notificationDrawerSlice";

interface Props {
  release: MarketplaceReleaseInstance;
  onClose: any;
  onDone: any;
}

export const ReleaseUninstallModal: React.FC<Props> = ({ release, onClose, onDone }) => {
  const org = useAppSelector(selectOrg);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const [confirmationText, setConfirmationText] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const inputRef = React.useRef<InputRef>(null);

  const CONFIRMATION_TOKEN = React.useMemo(() => {
    return `${release.name}`;
  }, [release.name]);

  const canDelete = confirmationText === CONFIRMATION_TOKEN;

  async function handleUninstall() {
    try {
      setIsLoading(true);
      await request({
        service: "marketplace",
        method: "post",
        url: `/helm/uninstall`,
        body: {
          org: release.org,
          name: release.name,
        },
      });
      notification.success({ message: "Success", description: "Uninstalled release" });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Uninstall",
        success: true,
        message: `Uninstalled release: ${release.name}`,
      }));
      setIsLoading(false);
      onDone();
      navigate(`/console/org/${org}/marketplace/release`);
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getMarketplaceErrorMessage(e);
      notification.warning({
        message: "Failed",
        description: (
          <div className="flex flex-col gap-2">
            {errorMessage.map((m) => (
              <div>{m}</div>
            ))}
          </div>
        ),
        duration: 20,
      });
      dispatch(addNotification({
        page: "Marketplace",
        action: "Uninstall",
        success: false,
        message: `Failed to uninstall release: ${release.name}`,
        details: errorMessage.join("\n"),
      }));
    }
  }

  const gvcDisplay = release.gvc && release.gvc !== " " ? release.gvc : null;

  return (
    <Modal
      title={
        <>
          Uninstall Release:{" "}
          <span className="font-bold" style={{ color: "var(--color-danger)" }}>
            {release.name}
          </span>
        </>
      }
      open={true}
      maskClosable={false}
      onCancel={onClose}
      onOk={handleUninstall}
      okText={`Uninstall`}
      cancelButtonProps={{ type: "default", danger: false, disabled: isLoading }}
      okButtonProps={{ type: "primary", danger: true, disabled: isLoading || !canDelete, loading: isLoading }}
      destroyOnHidden
      afterOpenChange={(open) => {
        if (open && inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <div className="flex flex-col gap-2">
        <div>Are you sure you want to uninstall this release? This action cannot be undone.</div>
        {gvcDisplay && (
          <div className="mt-2">
            <span className="font-medium">GVC:</span>{" "}
            <span className="font-bold" style={{ color: "var(--color-primary)" }}>
              {gvcDisplay}
            </span>
          </div>
        )}
        <div>
          <span className="font-medium">Release Name:</span>{" "}
          <span className="font-bold" style={{ color: "var(--color-primary)" }}>
            {release.name}
          </span>
        </div>
        <div className="delete-modal-confirmation-warning mt-4">{`Type "${CONFIRMATION_TOKEN}" for confirming the delete action`}</div>
        <Input
          ref={inputRef}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canDelete && !isLoading) {
              handleUninstall();
            }
          }}
          placeholder={`confirmation`}
        />
      </div>
    </Modal>
  );
};
